import { extractHomeType } from './home-type';
import { extractBedrooms, extractBathrooms, extractParkingSpaces } from './counts';
import { extractPriceRange } from './price';
import { extractSqftRange } from './sqft';
import { extractFeatures } from './features';
import { extractRental, extractStatus } from './rental-status';
import { extractLocker, extractMaintenanceFee } from './condo-extras';
import { extractHasImage, extractShowComingSoon } from './listing-flags';
import { extractListedSince } from './listed-since';
import { extractSort } from './sort';
import type { ExtractorOutput } from './types';
import type { MatchSpan, ParsedSearchFilters } from '../types';
import type { ParserPipelineFlags } from '../flags';

function mergePartial(
  target: Partial<ParsedSearchFilters>,
  source: Partial<ParsedSearchFilters>
): void {
  const { additional, ...rest } = source;
  Object.assign(target, rest);
  if (additional?.condoOrTownhouse) {
    target.additional = {
      ...target.additional,
      condoOrTownhouse: {
        ...target.additional?.condoOrTownhouse,
        ...additional.condoOrTownhouse,
      },
    };
  }
}

export interface RunFilterExtractorsOptions {
  flags: ParserPipelineFlags;
  now?: Date;
}

/**
 * Step 1 — run deterministic filter extractors. Each contributes spans; residual
 * is derived later from the combined span list only.
 *
 * Gated extras: `relativeDate` (listedSince / listedTo), `sortExtraction`.
 * Location runs separately (async) from the orchestrator when
 * `locationIdentification` is enabled.
 */
export function runFilterExtractors(
  query: string,
  options: RunFilterExtractorsOptions
): ExtractorOutput {
  const { flags, now = new Date() } = options;

  const extractors: Array<(q: string) => ExtractorOutput> = [
    extractHomeType,
    extractBedrooms,
    extractBathrooms,
    extractParkingSpaces,
    extractPriceRange,
    extractSqftRange,
    extractFeatures,
    extractRental,
    extractStatus,
    extractLocker,
    extractMaintenanceFee,
    extractHasImage,
    extractShowComingSoon,
  ];

  if (flags.relativeDate) {
    extractors.push(q => extractListedSince(q, now));
  }

  if (flags.sortExtraction) {
    extractors.push(extractSort);
  }

  const partial: Partial<ParsedSearchFilters> = {};
  const spans: MatchSpan[] = [];

  for (const extract of extractors) {
    const result = extract(query);
    mergePartial(partial, result.partial);
    spans.push(...result.spans);
  }

  return { partial, spans };
}
